import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../../styles/ThemeContext';
import DivisionSwitchLoader from './DivisionSwitchLoader';

// DEV-ONLY PREVIEW: lets us critique DivisionSwitchLoader in the real running app —
// toggle light/dark, replay the step sequence, change the division name — before wiring
// it into DivisionSwitcher.jsx. Not attached to any real flow. See App.js dev route block.
const DivisionSwitchLoaderPreview = () => {
  const { isDarkMode, setDarkMode, themeName, setTheme, allThemes } = useTheme();
  const [activeStep, setActiveStep] = useState(2);
  const [divisionName, setDivisionName] = useState('Finance');
  const [autoPlay, setAutoPlay] = useState(true);

  useEffect(() => {
    if (!autoPlay) return;
    const t1 = setTimeout(() => setActiveStep(3), 1200);
    const t2 = setTimeout(() => setActiveStep(4), 2800);
    const t3 = setTimeout(() => setActiveStep(2), 4600);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [autoPlay, activeStep === 2]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: isDarkMode ? '#1E1E1E' : '#EEEEEE' }}>
      {/* Fake app backdrop so the card's dimming/blur is visible against something */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `repeating-linear-gradient(0deg, ${isDarkMode ? '#2a2a2a' : '#ddd'} 0 2px, transparent 2px 40px),
                           repeating-linear-gradient(90deg, ${isDarkMode ? '#2a2a2a' : '#ddd'} 0 2px, transparent 2px 40px)`
      }} />

      <DivisionSwitchLoader divisionName={divisionName || 'division'} activeStep={activeStep} />

      {/* Portaled and mounted AFTER DivisionSwitchLoader's own portal, so it paints on top of
          the overlay's backdrop-filter/blur instead of being buried underneath it. */}
      {createPortal(
        <div style={{
          position: 'fixed', top: 16, left: 16, zIndex: 2000000,
          display: 'flex', flexDirection: 'column', gap: '10px',
          background: isDarkMode ? '#252526' : '#fff',
          border: `1px solid ${isDarkMode ? '#3A3A3A' : '#DDDDDD'}`,
          borderRadius: '12px', padding: '14px 16px',
          fontFamily: 'Poppins, sans-serif', fontSize: '13px',
          color: isDarkMode ? '#fff' : '#1a1a1a',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          minWidth: '220px'
        }}>
          <strong style={{ fontSize: '13px' }}>DivisionSwitchLoader Preview</strong>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={isDarkMode} onChange={(e) => setDarkMode(e.target.checked)} />
            Dark mode
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={autoPlay} onChange={(e) => setAutoPlay(e.target.checked)} />
            Auto-play steps
          </label>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '11px', opacity: 0.7 }}>Color theme</span>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {Object.entries(allThemes || {})
                .filter(([key]) => key !== 'default')
                .map(([key, t]) => (
                  <button
                    key={key}
                    title={t.name || key}
                    onClick={() => setTheme(key)}
                    style={{
                      width: '26px', height: '26px', borderRadius: '50%', cursor: 'pointer',
                      background: t.primary,
                      border: themeName === key ? '2.5px solid ' + (isDarkMode ? '#fff' : '#1a1a1a') : '2.5px solid transparent',
                      boxShadow: themeName === key ? '0 0 0 2px ' + t.primary + '55' : 'none',
                      padding: 0
                    }}
                  />
                ))}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span style={{ fontSize: '11px', opacity: 0.7 }}>Division name</span>
            <input
              type="text"
              value={divisionName}
              onChange={(e) => setDivisionName(e.target.value)}
              style={{
                padding: '6px 8px', borderRadius: '6px',
                border: `1px solid ${isDarkMode ? '#3A3A3A' : '#DDDDDD'}`,
                background: isDarkMode ? '#1E1E1E' : '#fff',
                color: isDarkMode ? '#fff' : '#1a1a1a'
              }}
            />
          </div>

          {!autoPlay && (
            <div style={{ display: 'flex', gap: '6px' }}>
              {[1, 2, 3, 4].map((s) => (
                <button
                  key={s}
                  onClick={() => setActiveStep(s)}
                  style={{
                    flex: 1, padding: '6px 0', borderRadius: '6px', cursor: 'pointer',
                    border: `1px solid ${s === activeStep ? (allThemes?.[themeName]?.primary || '#9404C3') : (isDarkMode ? '#3A3A3A' : '#DDDDDD')}`,
                    background: s === activeStep ? `${allThemes?.[themeName]?.primary || '#9404C3'}26` : 'transparent',
                    color: isDarkMode ? '#fff' : '#1a1a1a',
                    fontSize: '12px'
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default DivisionSwitchLoaderPreview;
